import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { DATA } from '@/data'
import Glitch from '@/components/Glitch'
import { AnimatePresence, motion } from 'framer-motion'

const navLinks = [
    { label: 'home',       href: '/' },
    { label: 'projects',   href: '/projects' },
    { label: 'about',      href: '/about' },
    { label: 'blog',       href: '/blog' },
    { label: 'resume',     href: '/resume' },
    { label: 'contact',    href: '/contact' },
]

/** Minimal hamburger — three lines that morph to an X */
function HamburgerIcon({ open }: { open: boolean }) {
    return (
        <span className="flex flex-col justify-center gap-[5px] w-5 h-5" aria-hidden>
            <span
                className={[
                    'block h-px bg-current transition-all duration-200 origin-center',
                    open ? 'rotate-45 translate-y-[6px]' : '',
                ].join(' ')}
            />
            <span
                className={[
                    'block h-px bg-current transition-all duration-200',
                    open ? 'opacity-0 scale-x-0' : '',
                ].join(' ')}
            />
            <span
                className={[
                    'block h-px bg-current transition-all duration-200 origin-center',
                    open ? '-rotate-45 -translate-y-[6px]' : '',
                ].join(' ')}
            />
        </span>
    )
}

export default function Navbar() {
    const location = useLocation()
    const [menuOpen, setMenuOpen] = useState(false)

    // Close menu on route change
    useEffect(() => {
        setMenuOpen(false)
    }, [location.pathname])

    // Prevent body scroll when menu is open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [menuOpen])

    return (
        <>
            <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex items-center justify-between h-12">

                        {/* Left: prompt logo */}
                        <Link to="/" className="flex items-center gap-1 font-mono text-sm group shrink-0">
                            <Glitch text="rapheal" trigger="hover" className="text-primary font-bold">rapheal</Glitch>
                            <span className="text-[#444]">@</span>
                            <Glitch text="portfolio" trigger="hover" className="text-[#666]">portfolio</Glitch>
                            <span className="text-[#444]">:~$</span>
                            <span
                                className="ml-1 inline-block w-2 h-4 bg-primary animate-blink"
                                aria-hidden="true"
                            />
                        </Link>

                        {/* Ambient status — visible only on very wide screens */}
                        <span className="hidden xl:flex items-center gap-3 text-[9px] font-mono text-[#333]">
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_4px_#00FF88] animate-blink-slow" />
                                SYS:ACTIVE
                            </span>
                            <span>34.05°N · 118.24°W</span>
                        </span>

                        {/* Desktop nav — hidden on mobile */}
                        <nav className="hidden md:flex items-center gap-0.5" aria-label="Main navigation">
                            {navLinks.map(({ label, href }) => {
                                const isActive = location.pathname === href
                                return (
                                    <Link
                                        key={href}
                                        to={href}
                                        className={[
                                            'px-3 py-1.5 text-xs font-mono rounded-sm transition-all duration-150',
                                            isActive
                                                ? 'text-primary border border-primary/30 bg-primary/5'
                                                : 'text-[#666] hover:text-foreground border border-transparent hover:border-border',
                                        ].join(' ')}
                                        aria-current={isActive ? 'page' : undefined}
                                    >
                                        <Glitch text={isActive ? `[${label}]` : label} trigger="hover">
                                            {isActive ? `[${label}]` : label}
                                        </Glitch>
                                    </Link>
                                )
                            })}

                            {/* GitHub icon link */}
                            <a
                                href={DATA.contact.social.GitHub.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="GitHub profile"
                                className="ml-2 px-2.5 py-1.5 text-xs font-mono text-[#666] border border-transparent
                                           hover:text-foreground hover:border-border rounded-sm transition-all duration-150"
                            >
                                <Glitch text="gh" trigger="hover">gh</Glitch>
                            </a>
                        </nav>

                        {/* Mobile hamburger button */}
                        <button
                            className="md:hidden flex items-center justify-center w-10 h-10 text-[#888]
                                       hover:text-primary transition-colors duration-150 rounded-sm
                                       border border-transparent hover:border-border touch-target"
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            aria-controls="mobile-nav-panel"
                            onClick={() => setMenuOpen(prev => !prev)}
                        >
                            <HamburgerIcon open={menuOpen} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu — overlay + slide-in panel */}
            <AnimatePresence>
                {menuOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            key="overlay"
                            className="mobile-nav-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setMenuOpen(false)}
                            aria-hidden="true"
                        />

                        {/* Panel */}
                        <motion.nav
                            id="mobile-nav-panel"
                            key="panel"
                            className="mobile-nav-panel"
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'tween', duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                            aria-label="Mobile navigation"
                        >
                            {/* Panel header */}
                            <div className="flex items-center justify-between mb-8">
                                <span className="font-mono text-xs text-[#444]">
                                    <span className="text-primary">rapheal</span>
                                    <span className="text-[#333]">@</span>
                                    <span className="text-[#555]">nav</span>
                                </span>
                                <button
                                    onClick={() => setMenuOpen(false)}
                                    aria-label="Close menu"
                                    className="text-[#555] hover:text-primary transition-colors touch-target"
                                >
                                    <HamburgerIcon open={true} />
                                </button>
                            </div>

                            {/* Nav links */}
                            <ul className="flex flex-col gap-1 mb-8">
                                {navLinks.map(({ label, href }, i) => {
                                    const isActive = location.pathname === href
                                    return (
                                        <motion.li
                                            key={href}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: 0.05 + i * 0.04 }}
                                        >
                                            <Link
                                                to={href}
                                                className={[
                                                    'flex items-center gap-2 px-3 py-3 font-mono text-sm rounded-sm',
                                                    'border transition-all duration-150 touch-target',
                                                    isActive
                                                        ? 'border-primary/30 bg-primary/5 text-primary'
                                                        : 'border-transparent text-[#666] hover:text-foreground hover:border-border',
                                                ].join(' ')}
                                                aria-current={isActive ? 'page' : undefined}
                                            >
                                                <span className={isActive ? 'text-primary' : 'text-[#333]'}>{'>'}</span>
                                                {isActive ? `[${label}]` : label}/
                                            </Link>
                                        </motion.li>
                                    )
                                })}
                            </ul>

                            {/* Social links */}
                            <div className="mt-auto pt-6 border-t border-[#1a1a1a]">
                                <p className="text-[10px] font-mono text-[#444] mb-3">$ ls --socials</p>
                                <div className="flex flex-col gap-2">
                                    <a
                                        href={DATA.contact.social.GitHub.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-mono text-[#555] hover:text-primary transition-colors py-1"
                                    >
                                        github.com/Rapheal-Kwabena
                                    </a>
                                    <a
                                        href={DATA.contact.social.LinkedIn.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-mono text-[#555] hover:text-primary transition-colors py-1"
                                    >
                                        linkedin.com/in/younggem
                                    </a>
                                    <a
                                        href={DATA.contact.social.Email.url}
                                        className="text-xs font-mono text-[#555] hover:text-primary transition-colors py-1"
                                    >
                                        raphealasomanikwabena@gmail.com
                                    </a>
                                </div>
                            </div>
                        </motion.nav>
                    </>
                )}
            </AnimatePresence>
        </>
    )
}
